import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import goalsApi from "../api/goalsApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";

const GoalDetailPage = () => {
  const { goalId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const [showGuide, setShowGuide] = React.useState(false);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadGoals = can(PERFORMANCE_PERMISSIONS.GOALS_READ);
  const canUpdateGoals = can(PERFORMANCE_PERMISSIONS.GOALS_UPDATE);

  if (!canReadGoals) {
    return (
      <ModulePageShell
        title="Goal Detail"
        description="View goal information, progress timeline, and add check-ins."
      >
        <EmptyState message="You do not have permission to view goal details." />
      </ModulePageShell>
    );
  }

  const goalQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.goalDetail, goalId],
    queryFn: () => goalsApi.getGoalById(goalId),
    enabled: Boolean(goalId),
  });

  const checkinMutation = useMutation({
    mutationFn: (payload) => goalsApi.submitGoalCheckin(goalId, payload),
    onSuccess: () => {
      toast({ title: "Check-in submitted", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.goalDetail, goalId],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to submit check-in",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      employeeId: "",
      progressPercentage: 0,
      achievedValue: 0,
      updateText: "",
      blockers: "",
      nextSteps: "",
    },
  });

  const goal = goalQuery.data?.data;
  const checkins = Array.isArray(goal?.checkins) ? goal.checkins : [];

  return (
    <ModulePageShell
      title="Goal Detail"
      description="View goal information, progress timeline, and add check-ins."
      onRefresh={() => goalQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {goalQuery.isLoading ? (
        <LoadingState />
      ) : (
        <>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="bold">{goal?.title || "Goal"}</Text>
              <Text color="gray.600">{goal?.description || "-"}</Text>
              <HStack>
                <Text fontSize="sm">Type: {goal?.goalType || "-"}</Text>
                <Text fontSize="sm">Status: {goal?.status || "-"}</Text>
                <Text fontSize="sm">
                  Progress: {goal?.progressPercentage || 0}%
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="semibold" mb={3}>
              Submit Check-in
            </Text>
            <form
              onSubmit={handleSubmit((values) =>
                checkinMutation.mutate(values),
              )}
            >
              <VStack spacing={3} align="stretch">
                <HStack>
                  <FormControl isRequired>
                    <FormLabel>Employee ID</FormLabel>
                    <Select
                      placeholder="Select employee"
                      {...register("employeeId")}
                      isDisabled={usersLoading}
                    >
                      {userOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Progress %</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      {...register("progressPercentage", {
                        valueAsNumber: true,
                      })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Achieved Value</FormLabel>
                    <Input
                      type="number"
                      {...register("achievedValue", { valueAsNumber: true })}
                    />
                  </FormControl>
                </HStack>
                <FormControl isRequired>
                  <FormLabel>Update</FormLabel>
                  <Input {...register("updateText")} />
                </FormControl>
                <HStack>
                  <FormControl>
                    <FormLabel>Blockers</FormLabel>
                    <Input {...register("blockers")} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Next Steps</FormLabel>
                    <Input {...register("nextSteps")} />
                  </FormControl>
                </HStack>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={checkinMutation.isPending}
                  isDisabled={!canUpdateGoals}
                >
                  Submit Check-in
                </Button>
              </VStack>
            </form>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
          >
            <Text fontWeight="semibold" mb={3}>
              Check-ins Timeline
            </Text>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Progress</Th>
                  <Th>Comment</Th>
                  <Th>Blockers</Th>
                </Tr>
              </Thead>
              <Tbody>
                {checkins.map((item) => (
                  <Tr key={item._id}>
                    <Td>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "-"}
                    </Td>
                    <Td>{item.progressPercentage ?? 0}%</Td>
                    <Td>{item.updateText || "-"}</Td>
                    <Td>{item.blockers || "-"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </ModulePageShell>
  );
};

export default GoalDetailPage;
